import './application-form.scss';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Application} from '../../backend/interfaces/application';
import {Box, FormLabel} from '@mui/material';
import {ReadOnlyTextField} from '../read-only-text-field/read-only-text-field';
import {translations} from '../../i18n/translations';
import {SmoothImage} from '../smooth-image/smooth-image';
import {appSizes} from '../../utils/constants/sizes';
import {ApplicationCategory} from '../../backend/interfaces/application-category';
import {ActivityType} from '../../backend/interfaces/activity-type';
import {ApplicationCategoriesRepository} from '../../repositories/application-categories';
import {ActivityTypesRepository} from '../../repositories/activity-types';
import {OfficeContextType} from '../../contexts/office/types';
import {OfficeContext} from '../../contexts/office/context';
import {AppLanguageTag} from '../../i18n/types';
import i18n from 'i18next';
import {LoadingContent} from '../loading-content/loading-content';

// TODO:
// - Edit/create mode for system administrators; (future)
// - Show the current version in Android and Windows for professionals caregivers; (future)

/**
 * Form to view, edit, remove, and create applications.
 *
 * It uses the office context to get the application's information.
 *
 * Permissions are given by props.
 *
 * Note: For now, we have only implemented the read only mode.
 */
export const ApplicationForm = (props: {canWrite: boolean}) => {
  // Translations
  const {t} = useTranslation();

  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [model, setModel] = useState<Application | undefined>();
  const [category, setCategory] = useState<ApplicationCategory | undefined>();
  const [activityType, setActivityType] = useState<ActivityType | undefined>();

  // Set application
  useEffect(() => {
    const init = async () => {
      if (officeContext.state.applicationForm) {
        // Clean up state
        setActivityType(undefined);
        setCategory(undefined);

        // Set model for view/edit/create
        if (officeContext.state.applicationForm.id) {
          // Get the application category from backend
          if (officeContext.state.applicationForm.categoryId) {
            try {
              // Get application category
              const applicationCategory: ApplicationCategory =
                await ApplicationCategoriesRepository.instance.getById(
                  officeContext.state.applicationForm.categoryId,
                  true
                );

              if (applicationCategory) {
                // Set state
                setCategory(applicationCategory);
              } else {
                // Error
                throw new Error('Empty application category.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get application category.', error);
            }
          }

          // Get the activity type from backend
          if (officeContext.state.applicationForm.activityTypeId) {
            try {
              // Get activity type
              const activityType: ActivityType = await ActivityTypesRepository.instance.getById(
                officeContext.state.applicationForm.activityTypeId,
                true
              );

              if (activityType) {
                // Set state
                setActivityType(activityType);
              } else {
                // Error
                throw new Error('Empty activity type.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get activity type.', error);
            }
          }

          // Set model for view/edit app
          setModel(officeContext.state.applicationForm);
        } else {
          // Set model for create app
          setModel(officeContext.state.applicationForm);
        }

        // Log
        console.debug('🆕 Application set in ApplicationForm.');
      }
    };

    // Init
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.applicationForm]);

  // Get name and description
  let nameLocalizedReadOnly: string = '';
  let descriptionLocalizedReadOnly: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      nameLocalizedReadOnly = model?.nameLocalized?.ptPT || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptPT || model?.description || '';
      break;
    case AppLanguageTag.esES:
      nameLocalizedReadOnly = model?.nameLocalized?.esES || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.esES || model?.description || '';
      break;
    case AppLanguageTag.glES:
      nameLocalizedReadOnly = model?.nameLocalized?.glES || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.glES || model?.description || '';
      break;
    case AppLanguageTag.enUS:
      nameLocalizedReadOnly = model?.nameLocalized?.enUS || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.enUS || model?.description || '';
      break;
    case AppLanguageTag.ptBR:
      nameLocalizedReadOnly = model?.nameLocalized?.ptBR || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptBR || model?.description || '';
      break;
    default:
      nameLocalizedReadOnly = model?.name || '';
      descriptionLocalizedReadOnly = model?.description || '';
      break;
  }

  // TODO: actually use the canRead prop to render an editable state in appropriate places (e.g., system admin view?).
  // For now the old behavior of only displaying read only inputs was kept.

  return (
    <>
      {/* Form */}
      {model ? (
        <>
          <Box className="application-form-line-1-container">
            <ReadOnlyTextField
              className="application-form-line-1-left"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.name_input)}
              value={nameLocalizedReadOnly}
            />
            <ReadOnlyTextField
              className="application-form-line-1-right"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.state_input)}
              value={
                model.isActive === true
                  ? t(translations.application_form.state_value_true)
                  : t(translations.application_form.state_value_false)
              }
            />
          </Box>
          <Box className="application-form-line-2-container">
            <ReadOnlyTextField
              className="application-form-line-2"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.description_input)}
              value={descriptionLocalizedReadOnly}
              multiline
            />
          </Box>
          <Box className="application-form-line-3-container">
            <ReadOnlyTextField
              className="application-form-line-3-left"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.category_input)}
              value={category?.name || ''}
            />
            <ReadOnlyTextField
              className="application-form-line-3-right"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.activity_type_input)}
              value={activityType?.name || ''}
            />
          </Box>
          <Box className="application-form-line-4-container">
            <ReadOnlyTextField
              className="application-form-line-4-left"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.available_for_user_types_input)}
              value={
                model.availableForUserTypes &&
                Array.isArray(model.availableForUserTypes) &&
                model.availableForUserTypes.length > 0
                  ? model.availableForUserTypes
                      .map((obj) => t(translations.user_types[obj]))
                      .join(', ')
                  : ''
              }
            />
            <ReadOnlyTextField
              className="application-form-line-4-right"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.is_system_application_input)}
              value={
                model.isSystemApplication === true
                  ? t(translations.application_form.is_system_application_value_yes)
                  : t(translations.application_form.is_system_application_value_no)
              }
            />
          </Box>
          <Box className="application-form-line-5-container">
            <ReadOnlyTextField
              className="application-form-line-5-left"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.android_key_input)}
              value={model.androidApplication?.packageName || ''}
            />
            <ReadOnlyTextField
              className="application-form-line-5-right"
              disabledTooltip={t(translations.application_form.input_disabled_tooltip)}
              label={t(translations.application_form.windows_key_input)}
              value={model.windowsApplication?.key || ''}
            />
          </Box>
          <Box className="application-form-line-6-container">
            <Box className="application-form-line-6-left">
              <FormLabel component="legend" className="application-form-application-picture-label">
                {t(translations.application_form.picture_input)}
              </FormLabel>
              <SmoothImage
                src={model.picture || ''}
                width={appSizes.appButtonSize.width}
                height={appSizes.appButtonSize.height}
                imageWidth={appSizes.appButtonSize.width}
                imageHeight={appSizes.appButtonSize.height}
                borderRadius={appSizes.appButtonSize.borderRadius}
              />
            </Box>
            <Box className="application-form-line-6-right">
              <FormLabel component="legend" className="application-form-application-icon-label">
                {t(translations.application_form.icon_input)}
              </FormLabel>
              <SmoothImage
                src={model.iconUrl || ''}
                width={appSizes.systemButtonSize.iconContainerEnabledSize.width}
                height={appSizes.systemButtonSize.iconContainerEnabledSize.height}
                imageWidth={appSizes.systemButtonSize.iconEnabledSize.width}
                imageHeight={appSizes.systemButtonSize.iconEnabledSize.height}
                backgroundColor={category?.systemButton.enabledStateColor || undefined}
                borderRadius={appSizes.systemButtonSize.innerCircleEnabledSize.borderRadius}
              />
            </Box>
          </Box>
        </>
      ) : (
        <LoadingContent />
      )}
    </>
  );
};
